import api from "@/lib/axios";

export const InterViewSlotService = {
  // Add this to the campaignsService object
  getAllSlots: async (vacancyId, start_time, statusId) => {
    try {
      let response;
      if (statusId) {
        response = await api.get(`/vacancy/${vacancyId}/status-events/${statusId}`, { params: { start_time } });
      } else {
        response = await api.get(`/vacancy/${vacancyId}/status-events`, { params: { start_time } });
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create interview schedules for recruiter
  createSlots: async (schedulesData) => {
    try {
      const response = await api.post("scheduling/recruiter/blocks", schedulesData);
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Failed to create interview schedules: " + error.message);
    }
  },

  // Get recruiter schedules (optional filters: vacancy_id, vacancy_status_id, company_id)
  getSchedules: async ({
    startDate,
    endDate,
    vacancyId,
    vacancyStatusId,
    companyId,
  } = {}) => {
    try {
      const params = {};
      if (startDate != null && startDate !== "") params.start_date = startDate;
      if (endDate != null && endDate !== "") params.end_date = endDate;
      if (vacancyId != null && vacancyId !== "") params.vacancy_id = vacancyId;
      if (vacancyStatusId != null && vacancyStatusId !== "") {
        params.vacancy_status_id = vacancyStatusId;
      }
      if (companyId != null && companyId !== "") params.company_id = companyId;

      const response = await api.get("scheduling/recruiter/blocks", { params });
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Failed to fetch schedules: " + error.message);
    }
  },

  // Get blocks by vacancy and status
  getVacancyStatusBlocks: async (vacancyId, statusId) => {
    try {
      const response = await api.get(`scheduling/recruiter/blocks/${vacancyId}/${statusId}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Failed to fetch blocks: " + error.message);
    }
  },

  // Delete all blocks for a vacancy + status (interview schedule row)
  deleteVacancyStatusBlocks: async (vacancyId, statusId) => {
    try {
      const response = await api.delete(`scheduling/recruiter/blocks/${vacancyId}/${statusId}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Failed to delete schedule: " + error.message);
    }
  },

  /** DELETE scheduling/recruiter/blocks/{blockId} — remove one availability block */
  deleteBlock: async (blockId) => {
    try {
      const response = await api.delete(`scheduling/recruiter/blocks/${blockId}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Failed to delete block: " + error.message);
    }
  },

  /** DELETE scheduling/recruiter/slots/{slotId} */
  deleteSlot: async (slotId) => {
    try {
      const response = await api.delete(`scheduling/recruiter/slots/${slotId}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Failed to delete slot: " + error.message);
    }
  },

  /** DELETE scheduling/recruiter/slots/{vacancyId}/{vacancyStatusId} with body { slot_ids } */
  deleteSlotsBulk: async (vacancyId, vacancyStatusId, slotIds) => {
    try {
      const response = await api.delete(`scheduling/recruiter/slots/${vacancyId}/${vacancyStatusId}`, {
        data: { slot_ids: slotIds },
      });
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Failed to delete slots: " + error.message);
    }
  },

  // Update a single block
  updateBlock: async (blockId, payload) => {
    try {
      const response = await api.put(`scheduling/recruiter/blocks/${blockId}`, payload);
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Failed to update block: " + error.message);
    }
  },

  // Book a slot
  bookSlot: async (slotId, payload) => {
    try {
      const response = await api.put(`scheduling/recruiter/slots/${slotId}`, payload);
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Failed to book slot: " + error.message);
    }
  },

  /** POST scheduling/recruiter/slots/{slotId}/generate-meeting */
  generateMeetingLink: async (slotId) => {
    try {
      const response = await api.post(
        `scheduling/recruiter/slots/${slotId}/generate-meeting`,
      );
      const data = response.data;

      if (data?.success === false) {
        throw new Error(
          data.error || data.message || "Failed to generate meeting",
        );
      }

      return data;
    } catch (error) {
      if (error.message && !error.response) {
        throw error;
      }
      const data = error.response?.data;
      if (data?.error) {
        throw new Error(data.error);
      }
      if (data?.message) {
        throw new Error(data.message);
      }
      throw new Error("Failed to generate meeting link: " + error.message);
    }
  },

  /**
   * Request / book a slot as candidate.
   * Tries the vacancy-scoped route first, then other registered variants on 404.
   * Primary: POST scheduling/candidate/{vacancyId}/{vacancyStatusId}/slots/{slotId}/request
   * @param {string|number} slotId
   * @param {{ vacancyId?: string|number, vacancyStatusId?: string|number }} [options]
   */
  requestSlot: async (slotId, options = {}) => {
    const { vacancyId, vacancyStatusId } = options;
    const attempts = [];

    // if (vacancyId != null && vacancyStatusId != null) {
    //   attempts.push(() =>
    //     api.post(`scheduling/candidate/${vacancyId}/${vacancyStatusId}/slots/${slotId}/request`)
    //   );
    //   attempts.push(() => api.post(`/${vacancyId}/${vacancyStatusId}/slots/${slotId}/request`));
    // }

    attempts.push(() => api.post(`scheduling/candidate/slots/${slotId}/request`));
    // attempts.push(() => api.post(`scheduling/candidate/slots/request`, { slot_id: slotId }));
    // attempts.push(() => api.post(`scheduling/candidate/slot/${slotId}/request`));

    let lastError = null;
    for (const run of attempts) {
      try {
        const response = await run();
        return response.data;
      } catch (error) {
        lastError = error;
        if (error?.response?.status === 404) continue;
        if (error.response?.data?.message) {
          throw new Error(error.response.data.message);
        }
        throw new Error("Failed to request slot: " + error.message);
      }
    }

    if (lastError?.response?.data?.message) {
      throw new Error(lastError.response.data.message);
    }
    throw new Error(
      "No candidate slot request route matched. Register scheduling/candidate/.../slots/.../request on the API or align routes."
    );
  },

  // Get candidate slots by vacancy and vacancy status
  getCandidateSlotsByVacancyAndStatus: async (vacancyId, vacancyStatusId) => {
    try {
      const response = await api.get(`/${vacancyId}/${vacancyStatusId}/slots`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Failed to fetch candidate slots: " + error.message);
    }
  },

  // Get candidate availability blocks for vacancy + status
  getCandidateAvailabilityBlocks: async (vacancyId, vacancyStatusId) => {
    try {
      const response = await api.get(`scheduling/candidate/availability-blocks/${vacancyId}/${vacancyStatusId}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Failed to fetch availability blocks: " + error.message);
    }
  },

  // Get contact requests for recruiter
  getContactRequests: async () => {
    try {
      const response = await api.get("scheduling/recruiter/contact-request");
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Failed to fetch contact requests: " + error.message);
    }
  },

  // Update contact request status
  updateContactRequestStatus: async (requestId, status) => {
    try {
      const response = await api.put(`scheduling/recruiter/contact-request/${requestId}`, { status });
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Failed to update status: " + error.message);
    }
  },

  /** GET notifications/contact-requests/{id}/messages — chat history, oldest first */
  getContactRequestMessages: async (contactRequestId) => {
    try {
      const response = await api.get(`notifications/contact-requests/${contactRequestId}/messages`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Failed to fetch messages: " + error.message);
    }
  },

  /** POST notifications/contact-requests/{id}/messages */
  sendContactRequestMessage: async (contactRequestId, { receiver_id, message }) => {
    try {
      const response = await api.post(
        `notifications/contact-requests/${contactRequestId}/messages`,
        {
          receiver_id: Number(receiver_id),
          message,
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Failed to send message: " + error.message);
    }
  },

  /** POST notifications/contact-requests/{id}/messages/read — mark thread read for current user */
  markContactRequestMessagesRead: async (contactRequestId) => {
    try {
      const response = await api.put(
        `notifications/contact-requests/${contactRequestId}/messages/read`,
      );
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Failed to mark messages read: " + error.message);
    }
  },
};

